"use client";

import { useEffect } from "react";

/** The language the Chinese surface declares. */
export const ZH_DOCUMENT_LANG = "zh-CN";

/**
 * WCAG 3.1.1 (Language of Page) for the `/zh` surface.
 *
 * WHAT THE WRAPPER DIV ALREADY FIXES — and what it cannot.
 * `app/(main)/zh/layout.tsx` renders `<div lang="zh-CN">` around every Chinese
 * page. That satisfies **WCAG 3.1.2 (Language of Parts)**: a screen reader
 * switches to Mandarin phonetics for the page *content*, which is the bulk of
 * the experience. It does NOT cover the page `<title>` — that lives in
 * `<head>`, outside the div, and is announced against the DOCUMENT language.
 * `app/layout.tsx` owns `<html lang="en-GB">`, and a nested App Router layout
 * cannot override an ancestor's `<html>` attributes. So a blind
 * Chinese-speaking visitor landing on `/zh` hears a genuinely Chinese title
 * ("利兹花店 | 鲜花配送、定制花束、永生花与钩织花") read with English
 * phonetics — an unusable announcement, and a real **3.1.1** failure.
 *
 * WHY THIS AND NOT A SEPARATE ROOT LAYOUT.
 * The textbook fix — a second root layout owning its own `<html lang="zh-CN">`
 * — was assessed and rejected: Next.js cannot soft-navigate between two root
 * layouts, so every `/zh` ↔ English link would become a hard document reload;
 * the English Header/Footer would then live inside a `zh-CN` document
 * (trading 3.1.1 for a fresh 3.1.2 failure); the Google-Translate crash guard
 * and theme-init script in `app/layout.tsx` would have to be duplicated; and
 * the shared root `not-found` boundary would be at risk. A one-attribute
 * mutation is a far smaller change with none of that blast radius.
 *
 * WHY MUTATING THE DOM IS SUFFICIENT.
 * Assistive technology reads the LIVE accessibility tree, not the served HTML,
 * so setting `documentElement.lang` after hydration is what a screen reader
 * actually observes. The crawler/SEO side of language is already handled
 * elsewhere and needs nothing from this component: `middleware.ts` emits
 * `Content-Language: zh-CN`, and hreflang + `og:locale` are correct.
 *
 * CONTRACT.
 *  - Mount: capture the CURRENT `documentElement.lang`, then set `zh-CN`.
 *  - Unmount: restore EXACTLY the captured value — never a hardcoded
 *    `"en-GB"`. Client-side navigation off `/zh` unmounts this component while
 *    the document persists, so restoring a guess would silently mislabel every
 *    English page reached from `/zh`. (Navigating `/zh/birthday` →
 *    `/zh/anniversary` keeps the layout mounted, so no thrash there.)
 *  - Reads and writes happen ONLY inside `useEffect` — touching `document`
 *    during render (or in a `useState` initializer) is the hydration-mismatch
 *    trap CLAUDE.md forbids. `app/layout.tsx` sets `suppressHydrationWarning`
 *    on `<html>`, so the post-hydration attribute change is safe.
 *  - Renders nothing.
 *
 * Guarded by `tests/unit/app/zh-document-lang.spec.tsx` (ZHDOC-1..8).
 */
export default function ZhDocumentLang() {
  useEffect(() => {
    const root = document.documentElement;
    // Captured at mount, restored verbatim on cleanup. `getAttribute` (not the
    // `.lang` property) so an absent attribute round-trips as absent rather
    // than becoming `lang=""`.
    const previous = root.getAttribute("lang");
    root.setAttribute("lang", ZH_DOCUMENT_LANG);
    return () => {
      if (previous === null) root.removeAttribute("lang");
      else root.setAttribute("lang", previous);
    };
  }, []);

  return null;
}
